import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { CheckCircle2, Circle, AlertTriangle, ChevronRight, ArrowLeft, Pin, PinOff } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useThemeStore } from '@/lib/theme-store';

/**
 * Three interchangeable workflow navigations for the ePCR.
 *
 * Each component receives the same props (sections, activeKey, onSelect,
 * getStatus, content) so the parent can swap them at runtime without
 * knowing which one is mounted. This keeps the ePCR page agnostic of
 * layout and lets the user pick the style that fits their apparatus.
 */

export interface WorkflowSection {
  key: string;
  label: string;
  /** Short label used in the command rail tooltip / small tiles. */
  shortLabel?: string;
  icon: LucideIcon;
  /** Logical grouping for the stepper. 'Intake' | 'Care' | 'Handoff' | 'Admin'. */
  phase: string;
}

export type SectionStatus = 'pending' | 'partial' | 'complete' | 'blocker';

/**
 * RailButton — workflow rail tab with cooling animation.
 *
 * When cockpit-v2 is on AND a section's status transitions from
 * non-complete → 'complete', the button briefly plays the 480 ms
 * `hot-steel-cool` keyframe (forge red → tempering amber → brushed
 * metal). After the animation completes the `.cooling` class is
 * removed and the button settles into its default brushed state.
 *
 * When cockpit-v2 is off, the existing v1 .rail-button-hot →
 * .rail-button transition is instant (one tick). No regression.
 */
function RailButton({
  active,
  status,
  onClick,
  className,
  children,
}: {
  active: boolean;
  status: SectionStatus;
  onClick: () => void;
  className?: string;
  children: ReactNode;
}) {
  const cockpitV2 = useThemeStore((s) => s.cockpitV2);
  const [cooling, setCooling] = useState(false);
  const prevStatus = useRef<SectionStatus>(status);

  useEffect(() => {
    if (!cockpitV2) {
      prevStatus.current = status;
      return;
    }
    // Trigger cooling animation on the transition into 'complete'.
    if (status === 'complete' && prevStatus.current !== 'complete') {
      setCooling(true);
      const t = window.setTimeout(() => setCooling(false), 520); // motion-cool + small buffer
      prevStatus.current = status;
      return () => window.clearTimeout(t);
    }
    prevStatus.current = status;
  }, [status, cockpitV2]);

  return (
    <button
      onClick={onClick}
      className={cn(
        'rail-button',
        cockpitV2 && 'rail-button-v2',
        active && 'rail-button-active',
        // Hot states only when not active (active = engaged blue plate
        // takes precedence). Cooling overrides hot — they're mutually
        // exclusive — and is only applied on the v2 surface.
        !active && !cooling && status === 'blocker' && 'rail-button-hot-blocker',
        !active && !cooling && (status === 'pending' || status === 'partial') && 'rail-button-hot',
        cockpitV2 && cooling && 'cooling',
        className,
      )}
    >
      {children}
    </button>
  );
}

export interface WorkflowLayoutProps {
  sections: WorkflowSection[];
  activeKey: string;
  onSelect: (key: string) => void;
  getStatus?: (key: string) => SectionStatus;
  content: ReactNode;
}

// ============================================================
// Option A — Left Rail Stepper
// ============================================================
// Always-visible vertical numbered rail on the left. Sections are
// grouped into phases. Each row is a big touch target. A progress
// bar runs down the rail showing overall completion. Validation state
// colors the status dot (pending / partial / complete / blocker).
export function LeftRailWorkflow({
  sections, activeKey, onSelect, getStatus, content,
}: WorkflowLayoutProps) {
  // Group by phase in order of first appearance
  const phases: Array<{ name: string; sections: WorkflowSection[] }> = [];
  for (const s of sections) {
    const last = phases[phases.length - 1];
    if (last && last.name === s.phase) last.sections.push(s);
    else phases.push({ name: s.phase, sections: [s] });
  }

  const complete = sections.filter((s) => getStatus?.(s.key) === 'complete').length;
  const pct = Math.round((complete / sections.length) * 100);

  // The workflow rail is "pinned" by default on desktop (always
  // visible alongside the content) and unpinned/closed on mobile
  // (slide-out drawer). Toggle is the pin/unpin button INSIDE the
  // rail header — no more floating overlay pill obstructing the
  // content area. When unpinned on desktop the rail collapses
  // entirely; a thin restore tab on the left edge brings it back.
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(min-width: 1024px)').matches;
  });

  return (
    <div className="flex gap-0 relative">
      {/* Backdrop — only on small screens when open, so a drawer over
          a phone-width screen can dismiss with a tap-outside. Desktop
          can leave the rail open without a backdrop covering the
          content pane. */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/50"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Restore tab — visible only when the rail is closed. Desktop
          renders a thin vertical handle on the left edge (unobtrusive,
          fits in the small gutter). Mobile renders a small icon
          button at top-left — same affordance, smaller. */}
      {!open && (
        /* Binder-tab restore handle — labeled vertical tab anchored
           to the left edge of the content pane. Looks like a tab on
           the spine of a binder. Visible at every breakpoint with
           the label rotated 90° (writing-mode: vertical-rl) so it
           reads top-to-bottom along the tab. Tap to slide the rail
           back open. */
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cn(
            'absolute top-3 left-0 z-30',
            'flex flex-col items-center gap-1.5 px-1 py-3',
            'rail-button !rounded-l-none !rounded-r-md',
            'shadow-lg hover:translate-x-0.5 transition-transform',
          )}
          title="Show workflow rail — pin to keep open"
          aria-label="Show workflow rail"
        >
          <Pin size={13} className="text-[var(--cockpit-text-on-panel-2)]" />
          <span
            className="text-[10px] font-bold uppercase tracking-widest text-[var(--cockpit-text-on-panel)]"
            style={{ writingMode: 'vertical-rl' }}
          >
            Workflow
          </span>
          <span className="text-[9px] font-mono text-[var(--cockpit-text-on-panel-3)]">
            {complete}/{sections.length}
          </span>
        </button>
      )}

      {/* Cockpit workflow rail. Desktop ≥ lg: in-flow alongside the
          content (no overlay). Mobile/tablet: fixed-position drawer
          that slides in from the left over the content. Either way
          the toggle pill controls visibility. */}
      <aside
        id="workflow-rail"
        className={cn(
          'rail-cockpit w-40 flex flex-col',
          // Desktop: in-flow when open, hidden (zero width) when closed.
          // Use display:none rather than translate-x to give the content
          // pane the full viewport width while collapsed.
          open ? 'lg:relative lg:flex lg:shrink-0' : 'lg:hidden',
          'lg:translate-x-0 lg:shadow-none',
          // Mobile: always fixed-position drawer; translate controls
          // visibility so the slide animation reads cleanly.
          // max-h uses 100dvh so iOS Safari's URL-bar collapse doesn't
          // clip the rail; offset by --shell-banner-h so the rail sits
          // directly under AppShell's two-row banner instead of behind it.
          'fixed left-0 z-40 shadow-2xl transition-transform duration-200 ease-out lg:transition-none lg:max-h-none',
          // Mobile/tablet: rail starts below the banner and runs to the
          // bottom of the dynamic viewport. On desktop the rail goes
          // back to in-flow (lg:relative below) so we reset top/bottom.
          '[top:var(--shell-banner-h,120px)] bottom-0 [max-height:calc(100dvh-var(--shell-banner-h,120px))]',
          'lg:!top-auto lg:!bottom-auto',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-hidden={open ? undefined : 'true'}
      >
        {/* Progress header — compact engraved-plate look. Pin/unpin
            sits inline with the title; clicking it collapses the
            rail (which on desktop reveals the small restore tab on
            the content pane's left edge, and on mobile dismisses
            the drawer). */}
        <div className="border-b border-[var(--cockpit-edge-deep)] px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--cockpit-text-on-panel-3)]">
              Workflow
            </div>
            <div className="flex items-center gap-2">
              <div className="font-mono text-[11px] text-[var(--cockpit-text-on-panel)] tabular-nums">
                {complete}/{sections.length}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rail-button-mini text-[var(--cockpit-text-on-panel-2)] hover:text-[var(--cockpit-text-on-panel)]"
                title="Unpin workflow rail (collapse)"
                aria-label="Unpin workflow rail"
              >
                <PinOff size={12} />
              </button>
            </div>
          </div>
          <div className="mt-1.5 h-1 w-full bg-black/40 overflow-hidden rounded-sm">
            <div
              className="h-full bg-blue-500 transition-[width]"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Phase groups — header row is a recessed plate. Each step
            below is a brushed-metal button, with the status carried
            by a colored left-edge stripe (blue=active, red=blocker,
            amber=pending/partial, emerald=complete). */}
        <div className="flex-1 overflow-y-auto px-1.5 py-2 space-y-2">
          {phases.map((p) => (
            <div key={p.name} className="space-y-1">
              <div className="px-2 pt-0.5 pb-1 text-[9px] font-bold uppercase tracking-widest text-[var(--cockpit-text-on-panel-3)]">
                {p.name}
              </div>
              {p.sections.map((sec) => {
                const status = getStatus?.(sec.key) ?? 'pending';
                const active = sec.key === activeKey;
                return (
                  <RailButton
                    key={sec.key}
                    active={active}
                    status={status}
                    onClick={() => {
                      onSelect(sec.key);
                      // Auto-dismiss the drawer on small screens so the
                      // input fields get full width once the section is
                      // chosen. Desktop keeps the rail open since the
                      // user explicitly opened it for the session.
                      if (typeof window !== 'undefined' &&
                          !window.matchMedia('(min-width: 1024px)').matches) {
                        setOpen(false);
                      }
                    }}
                    className="w-full flex items-center text-left text-[13px] font-semibold min-h-[44px] px-2.5 py-1.5"
                  >
                    <span className="flex-1 leading-snug whitespace-normal">
                      {sec.label}
                    </span>
                  </RailButton>
                );
              })}
            </div>
          ))}
        </div>
      </aside>

      {/* Content pane. Top-left edge reserves a thin gutter for the
          desktop "restore" tab when the rail is unpinned; mobile
          doesn't need the gutter since the rail re-opens via the
          AppShell's hamburger. */}
      <div className={cn('flex-1 min-w-0 p-4', !open && 'lg:pl-8')}>{content}</div>
    </div>
  );
}

/**
/* statusBoxShadow() removed — left-edge color stripe was replaced by
   the full hot-steel button variant (.rail-button-hot /
   .rail-button-hot-blocker) so the whole row signals state. */

// ============================================================
// Option B — Grid Launcher
// ============================================================
// When no section is selected, the user sees a compact tile board
// with each section as a square tile (icon + label + validation).
// Tap a tile → enter that section full-width with a "< Back to board"
// control in the header. Great for MDT-style decisive drill-in.
export function GridLauncherWorkflow({
  sections, activeKey, onSelect, getStatus, content,
}: WorkflowLayoutProps) {
  const [browsing, setBrowsing] = useState(false);

  if (browsing) {
    const active = sections.find((s) => s.key === activeKey);
    return (
      <div className="border border-slate-700 bg-slate-950">
        <div className="flex items-center justify-between border-b border-slate-700 bg-slate-900 px-4 py-2">
          <button
            onClick={() => setBrowsing(false)}
            className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-slate-300 hover:text-slate-100 min-h-[44px]"
          >
            <ArrowLeft size={14} />
            Board
          </button>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-200">
            {active && <active.icon size={14} className="text-blue-400" />}
            {active?.label ?? '—'}
          </div>
          <SectionStepper
            sections={sections}
            activeKey={activeKey}
            onSelect={onSelect}
          />
        </div>
        <div className="p-4">{content}</div>
      </div>
    );
  }

  // Board view
  const complete = sections.filter((s) => getStatus?.(s.key) === 'complete').length;
  const blockers = sections.filter((s) => getStatus?.(s.key) === 'blocker').length;
  return (
    <div className="border border-slate-700 bg-slate-950">
      <div className="flex items-center justify-between border-b border-slate-700 bg-slate-900 px-4 py-2">
        <span className="text-xs font-bold uppercase tracking-widest text-slate-300">
          Sections
        </span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
          {complete}/{sections.length} complete{blockers > 0 && ` · ${blockers} blocking`}
        </span>
      </div>
      {/* Tile grid: 2 cols on phones, 3 on iPad portrait, 4 on iPad Pro
          portrait, 5 on laptops and landscape. The edge border modulo
          below is intentionally aligned to the LARGEST column count so
          we don't over-cut borders when the grid is narrower. */}
      <div className="grid grid-cols-2 ipad:grid-cols-3 ipadpro:grid-cols-4 xl:grid-cols-5 gap-0 border-t border-slate-800">
        {sections.map((sec, i) => {
          const status = getStatus?.(sec.key) ?? 'pending';
          return (
            <button
              key={sec.key}
              onClick={() => { onSelect(sec.key); setBrowsing(true); }}
              className={cn(
                'relative flex flex-col items-start gap-2 p-4 text-left min-h-[120px] border-r border-b border-slate-800 transition-colors',
                // Inverted hierarchy: unfinished tiles POP, complete
                // tiles quietly sit in the background.
                status === 'blocker'  && 'bg-red-950/30 hover:bg-red-950/50',
                status === 'pending'  && 'bg-amber-950/20 hover:bg-amber-950/30',
                status === 'partial'  && 'bg-amber-950/10 hover:bg-amber-950/20',
                status === 'complete' && 'bg-slate-900/40 hover:bg-slate-900',
              )}
            >
              <div className="flex items-center justify-between w-full">
                <span className={cn(
                  'font-mono text-[10px] tabular-nums',
                  status === 'complete' ? 'text-slate-600' :
                  status === 'blocker' ? 'text-red-400' :
                  'text-amber-300',
                )}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <StatusDot status={status} />
              </div>
              <sec.icon
                size={22}
                className={cn(
                  status === 'blocker'  ? 'text-red-400' :
                  status === 'pending'  ? 'text-amber-300' :
                  status === 'partial'  ? 'text-amber-300' :
                  'text-slate-500',
                )}
              />
              <div className={cn(
                'text-sm uppercase tracking-wider leading-tight',
                status === 'complete'
                  ? 'font-normal text-slate-400'
                  : 'font-bold text-slate-100',
              )}>
                {sec.label}
              </div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-slate-600 mt-auto">
                {sec.phase}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// Option C — Command Rail (right-side floating icon rail)
// ============================================================
// Right-side floating vertical rail of icons. 56-px wide, always on top
// of the content area. Tablet-oriented: stays in right-thumb reach while
// the crew holds the device. Icons are grouped by phase with thin
// separator lines. Hover / long-press reveals the full label.
export function CommandRailWorkflow({
  sections, activeKey, onSelect, getStatus, content,
}: WorkflowLayoutProps) {
  return (
    <div className="relative border border-slate-700 bg-slate-950">
      {/* Pad the right side so content never hides behind the floating rail */}
      <div className="p-4 pr-16 ipad:pr-20 ipadpro:pr-24">{content}</div>
      {/* Rail: 56px phones, 64px iPad, 72px iPad Pro — bigger targets on
          bigger tablets and matched pad above keeps content clear. */}
      <aside className="absolute top-0 right-0 h-full w-14 ipad:w-16 ipadpro:w-[72px] border-l border-slate-700 bg-slate-950 flex flex-col items-center py-2 gap-0.5">
        {sections.map((sec, idx) => {
          const status = getStatus?.(sec.key) ?? 'pending';
          const active = sec.key === activeKey;
          // Insert phase separator before the first section of a new phase
          const phaseChanged = idx > 0 && sections[idx - 1].phase !== sec.phase;
          return (
            <div key={sec.key} className="contents">
              {phaseChanged && <div className="w-6 my-1 border-t border-slate-700" />}
              <button
                onClick={() => onSelect(sec.key)}
                title={sec.label}
                aria-label={sec.label}
                className={cn(
                  'relative flex items-center justify-center transition-colors',
                  'h-11 w-11 ipad:h-12 ipad:w-12 ipadpro:h-14 ipadpro:w-14',
                  // Active wins the visual
                  active && 'bg-blue-900/40 text-blue-200 border border-blue-600',
                  // Unfinished = amber tint + icon pops
                  !active && (status === 'pending' || status === 'partial') &&
                    'bg-amber-950/30 text-amber-200 border border-amber-700 hover:bg-amber-950/50',
                  !active && status === 'blocker' &&
                    'bg-red-950/40 text-red-200 border border-red-700 hover:bg-red-950/60',
                  // Complete = plain
                  !active && status === 'complete' &&
                    'text-slate-500 hover:bg-slate-900 hover:text-slate-300 border border-transparent',
                )}
              >
                <sec.icon size={20} />
                {/* Status corner indicator — only for unfinished items */}
                {(status === 'pending' || status === 'partial' || status === 'blocker') && (
                  <span className={cn(
                    'absolute top-1 right-1 h-1.5 w-1.5',
                    status === 'blocker' ? 'bg-red-500 animate-pulse' : 'bg-amber-400',
                  )} />
                )}
              </button>
            </div>
          );
        })}
      </aside>
    </div>
  );
}

// ============================================================
// Shared bits
// ============================================================

function StatusDot({ status }: { status: SectionStatus }) {
  // Inverted visual hierarchy: unfinished states DEMAND attention,
  // completed state is intentionally understated (quiet checkmark).
  if (status === 'blocker')  return <AlertTriangle size={14} className="text-red-500 animate-pulse" />;
  if (status === 'pending')  return <Circle size={14} className="text-amber-400 fill-amber-400/50" />;
  if (status === 'partial')  return <Circle size={14} className="text-amber-300 fill-amber-400/30" />;
  // Complete: muted check — the "done, move on" signal
  return <CheckCircle2 size={14} className="text-slate-500" />;
}

/** Compact prev/next stepper used inside the Grid-Launcher header. */
function SectionStepper({
  sections, activeKey, onSelect,
}: {
  sections: WorkflowSection[];
  activeKey: string;
  onSelect: (k: string) => void;
}) {
  const idx = sections.findIndex((s) => s.key === activeKey);
  const prev = idx > 0 ? sections[idx - 1] : null;
  const next = idx < sections.length - 1 ? sections[idx + 1] : null;
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => prev && onSelect(prev.key)}
        disabled={!prev}
        className="min-h-[36px] min-w-[36px] flex items-center justify-center text-slate-400 hover:text-slate-100 disabled:opacity-30"
        title={prev ? `Prev: ${prev.label}` : undefined}
        aria-label="Previous section"
      >
        <ChevronRight size={14} className="rotate-180" />
      </button>
      <span className="font-mono text-[10px] tabular-nums text-slate-500 px-1">
        {idx + 1}/{sections.length}
      </span>
      <button
        onClick={() => next && onSelect(next.key)}
        disabled={!next}
        className="min-h-[36px] min-w-[36px] flex items-center justify-center text-slate-400 hover:text-slate-100 disabled:opacity-30"
        title={next ? `Next: ${next.label}` : undefined}
        aria-label="Next section"
      >
        <ChevronRight size={14} />
      </button>
    </div>
  );
}
